import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import MutationKeys from 'hooks/mutations/keys';
import QueryKeys from 'hooks/queries/keys';
import useMutation from 'hooks/useMutation';
import { ApiMethods, CuredApiPaths } from 'models/enums';

import type { SavedRowTemplateLinksResponse } from 'hooks/queries/useSavedRowTemplateLinks';

export type LinkSavedRowTemplateInput = {
    templateId: number;
    rowGuid: string;
    savedRowId: string;
    name: string;
    category: string;
};

/** POST /saved-rows/template-row-link — upserts `template_rows` (template_id, row_guid) → saved_row. */
const useLinkSavedRowTemplateLink = (mutateOptions?: UseMutationOptions<void, Error, LinkSavedRowTemplateInput>) => {
    const queryClient = useQueryClient();
    const { onSuccess: onSuccessOption, ...rest } = mutateOptions ?? {};

    return useMutation<LinkSavedRowTemplateInput, void>(
        MutationKeys.LINK_SAVED_ROW_TEMPLATE,
        `${CuredApiPaths.SAVED_ROWS}/template-row-link`,
        {
            onSuccess: (_data, variables, context) => {
                queryClient.setQueryData<SavedRowTemplateLinksResponse>(
                    QueryKeys.SAVED_ROWS_TEMPLATE_LINKS(variables.templateId),
                    (prev) => {
                        const restLinks = (prev?.links ?? []).filter((l) => l.rowGuid !== variables.rowGuid);
                        return {
                            links: [
                                ...restLinks,
                                {
                                    rowGuid: variables.rowGuid,
                                    savedRowId: variables.savedRowId,
                                    name: variables.name,
                                    category: variables.category,
                                },
                            ],
                        };
                    }
                );
                onSuccessOption?.(_data, variables, context);
            },
            ...rest,
        },
        ApiMethods.POST
    );
};

export default useLinkSavedRowTemplateLink;
